// TypeScript
// Enhanced composable: SSR-safe, idempotent, Promise-cached loader, internal Map tracking, optional DOM attribute set for compatibility.
import { appEnv } from 'config/app'
import { sleep } from '@/utils/sleep'

type TurnstileAPI = {
  render: (container: string | HTMLElement, opts: Record<string, any>) => string
  getResponse: (widgetId: string) => string | null
  reset?: (widgetId?: string) => void
  remove?: (widgetId: string) => void
}

const TEST_SITE_KEY = '1x00000000000000000000AA'

// A blocked or slow script load must not stick for the rest of the page's
// life: a failed attempt removes its script tag and the cached promise, so the
// next attempt, or a later render, starts afresh.
const RETRY_DELAYS_MS = [2000, 5000]
const INIT_TIMEOUT_MS = 10000
const TOKEN_POLL_MS = 250

function resolveSiteKey(explicit?: string) {
  // Prefer explicit, then configured env key from appEnv, then test key in dev
  if (explicit) return explicit
  const envKey = appEnv.turnstileSiteKey
  if (envKey && envKey.trim().length > 0) return envKey.trim()
  return import.meta.env.DEV ? TEST_SITE_KEY : '' // prod without key should be empty -> fail fast
}

export function useTurnstile() {
  const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined'

  const state: {
    scriptLoaded: boolean
    loadingPromise?: Promise<void>
    // Track containerId -> widgetId
    widgets: Map<string, string>
  } = (globalThis as any).__useTurnstileState || {
    scriptLoaded: isBrowser && typeof (window as any).turnstile !== 'undefined',
    loadingPromise: undefined,
    widgets: new Map<string, string>()
  }
  ;(globalThis as any).__useTurnstileState = state

  const loadOnce = () =>
    new Promise<void>((resolve, reject) => {
      // A script removed at its deadline may still finish later; once the API
      // is up there is nothing left to load.
      if ((window as any).turnstile) {
        state.scriptLoaded = true
        resolve()
        return
      }
      const existing = document.querySelector<HTMLScriptElement>(
        'script[src*="challenges.cloudflare.com/turnstile"]'
      )
      if (existing) {
        if ((window as any).turnstile) {
          state.scriptLoaded = true
          resolve()
          return
        }
        const t = setInterval(() => {
          if (typeof window === 'undefined') {
            clearInterval(t)
            return
          }
          if ((window as any).turnstile) {
            clearInterval(t)
            state.scriptLoaded = true
            resolve()
          }
        }, 100)
        setTimeout(() => {
          clearInterval(t)
          if (typeof window === 'undefined') return
          if ((window as any).turnstile) {
            state.scriptLoaded = true
            resolve()
          } else {
            existing.remove()
            reject(new Error('Turnstile script present but window.turnstile did not initialize'))
          }
        }, INIT_TIMEOUT_MS)
        return
      }

      const script = document.createElement('script')
      // Explicit rendering provides full control and stable callbacks
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      // One deadline covers the fetch and the init: a request that never
      // answers would otherwise neither load nor fail, and never retry.
      const deadline = Date.now() + INIT_TIMEOUT_MS
      const fail = (message: string) => {
        script.remove()
        reject(new Error(message))
      }
      const stalled = setTimeout(() => {
        if ((window as any).turnstile) {
          state.scriptLoaded = true
          resolve()
        } else {
          fail('Turnstile script did not load in time')
        }
      }, INIT_TIMEOUT_MS)
      script.onload = () => {
        clearTimeout(stalled)
        const check = () => {
          if ((window as any).turnstile) {
            state.scriptLoaded = true
            resolve()
          } else if (Date.now() > deadline) {
            fail('Turnstile loaded but did not initialize')
          } else {
            setTimeout(check, 50)
          }
        }
        check()
      }
      script.onerror = () => {
        clearTimeout(stalled)
        fail('Turnstile script failed to load')
      }
      document.head.appendChild(script)
    })

  const loadScript = () => {
    if (!isBrowser) return Promise.resolve() // SSR no-op
    if (state.scriptLoaded) return Promise.resolve()
    if (state.loadingPromise) return state.loadingPromise

    state.loadingPromise = (async () => {
      for (const delay of RETRY_DELAYS_MS) {
        try {
          return await loadOnce()
        } catch {
          await sleep(delay)
        }
      }
      return loadOnce()
    })().catch((err) => {
      state.loadingPromise = undefined
      throw err
    })
    return state.loadingPromise
  }

  type RenderOptions = {
    sitekey?: string
    theme?: 'auto' | 'light' | 'dark'
    appearance?: 'always' | 'execute' | 'interaction-only'
    retry?: 'auto' | 'never'
    'refresh-expired'?: 'auto' | 'manual'
    callback?: (token: string) => void
    'expired-callback'?: () => void
    'error-callback'?: (code: string) => void
    // Any additional options Cloudflare supports
    [k: string]: any
  }

  const renderOnce = async (
    containerId: string,
    sitekey?: string,
    options?: RenderOptions
  ): Promise<{ widgetId: string | null; alreadyRendered: boolean }> => {
    if (!isBrowser) throw new Error('Turnstile cannot render during SSR')

    // Prevent double-render for same container via internal Map
    const existingWidFromMap = state.widgets.get(containerId)
    if (existingWidFromMap) {
      return { widgetId: existingWidFromMap, alreadyRendered: true }
    }

    // Genuine misuse: the container was never mounted
    const container = document.getElementById(containerId)
    if (!container) {
      throw new Error(`Turnstile container "${containerId}" not found`)
    }

    // The element, not its id, decides whether this render still has an
    // owner: a form remounted during the load reuses the id, and the
    // unmounted one's render must not fill the new element with its callbacks.
    try {
      await loadScript()
    } catch (err) {
      if (!container.isConnected) return { widgetId: null, alreadyRendered: false }
      throw err
    }
    if (!container.isConnected) return { widgetId: null, alreadyRendered: false }

    // A second render queued behind the same load (a retry, a resubmit) must
    // not put another widget into the container the first one filled.
    const renderedMeanwhile = state.widgets.get(containerId)
    if (renderedMeanwhile) {
      return { widgetId: renderedMeanwhile, alreadyRendered: true }
    }

    const resolvedSiteKey = resolveSiteKey(sitekey)
    if (!resolvedSiteKey) {
      // In production, absence of a site key should be surfaced clearly
      throw new Error(
        'Turnstile site key missing. Set VITE_TURNSTILE_SITE_KEY in the environment for production.'
      )
    }

    const api: TurnstileAPI | undefined = (window as any).turnstile
    if (!api) throw new Error('window.turnstile is not available after loading script')

    const wid = api.render(container, {
      sitekey: resolvedSiteKey,
      theme: options?.theme ?? 'auto',
      appearance: options?.appearance ?? 'always',
      retry: options?.retry ?? 'auto',
      'refresh-expired': options?.['refresh-expired'] ?? 'auto',
      callback: options?.callback,
      'expired-callback': options?.['expired-callback'],
      'error-callback': options?.['error-callback'],
      ...options
    })

    if (wid) {
      state.widgets.set(containerId, String(wid))
      // Backward compatibility: expose widget id as data attribute for existing code paths
      try {
        container.setAttribute('data-turnstile-widget-id', String(wid))
      } catch {
        // ignore: attribute set is best-effort for backward compatibility
      }
    }

    return { widgetId: wid ?? null, alreadyRendered: false }
  }

  const getResponse = (widgetId: string) => {
    const api: TurnstileAPI | undefined = (isBrowser ? (window as any).turnstile : undefined)
    if (!api) return null
    try {
      return api.getResponse(widgetId)
    } catch {
      return null
    }
  }

  /**
   * Resolves with the token `read` returns once Cloudflare has issued one, or
   * with '' after timeoutMs. `read` runs on every poll because the widget may
   * still be loading or solving when a form is submitted.
   */
  const waitForToken = (read: () => string | null | undefined, timeoutMs: number) =>
    new Promise<string>((resolve) => {
      const deadline = Date.now() + timeoutMs
      const poll = () => {
        const token = read()
        if (token) resolve(token)
        else if (Date.now() >= deadline) resolve('')
        else setTimeout(poll, TOKEN_POLL_MS)
      }
      poll()
    })

  const reset = (widgetId?: string) => {
    const api: TurnstileAPI | undefined = (isBrowser ? (window as any).turnstile : undefined)
    if (!api || typeof api.reset !== 'function') return
    try {
      api.reset(widgetId)
    } catch {
      // ignore
    }
  }

  /**
   * Tear down a rendered widget. Takes the CONTAINER id (or element) — the
   * same id passed to renderOnce — NOT the Cloudflare widget id. Clears the
   * internal registry entry so renderOnce can re-render on the next mount;
   * safe to call when nothing was rendered.
   */
  const destroy = (container: string | Element) => {
    if (!isBrowser) return
    const id = typeof container === 'string' ? container : container.id
    if (!id) return
    const wid = state.widgets.get(id)
    state.widgets.delete(id)
    const api: TurnstileAPI | undefined = (window as any).turnstile
    if (wid && typeof api?.remove === 'function') {
      try {
        api.remove(wid)
      } catch {
        // ignore: the container is emptied below either way
      }
    }
    const el = typeof container === 'string' ? document.getElementById(container) : (container as Element)
    if (el) {
      // remove any rendered child nodes (iframe etc.)
      try {
        ;(el as HTMLElement).innerHTML = ''
        el.removeAttribute('data-turnstile-widget-id')
      } catch {
        // ignore: DOM cleanup best-effort
      }
    }
  }

  return { loadScript, renderOnce, getResponse, waitForToken, reset, destroy, _state: state }
}
